// URL Normalizer
// ---------------
// Reduces a URL to the canonical form the BE uses when it checks for
// duplicate webapp URLs. Matching the BE spec exactly is the whole
// point — if our normalizer disagrees with theirs, the FE's
// "duplicate already exists" pre-check either misses a real dupe (BE
// rejects the create) or fires on a non-dupe (BE accepts but the FE
// blocks). The 18-case spec lives in the URL spot-test brief.
//
// Rules applied:
//   * scheme   lowercased       https://x.com   == HTTPS://x.com
//   * host     lowercased       https://x.com   == https://X.com
//   * port     default stripped https://x.com   == https://x.com:443
//                               (non-default ports preserved)
//   * path     trailing /'s stripped (multi-slash too), empty path OK
//   * query    preserved verbatim, including leading "?"
//   * fragment preserved verbatim, including leading "#"
//   * whitespace trimmed before parsing
//
// Rules NOT applied (distinct identities):
//   * www vs apex   (www.x.com != x.com)
//   * http vs https (http://x.com != https://x.com)
//   * path case     (/Auth != /auth — HTTP paths are case-sensitive)
//
// Unparseable input is returned trimmed-as-is; the calling form's
// URL-format validator catches that path. We don't want this helper
// to mask format errors as "looks fine" — the duplicate check is a
// belt; the URL whitelist regex is the braces.

const HTTPS_DEFAULT_PORT = "443";
const HTTP_DEFAULT_PORT = "80";

export function normalizeUrl(input: string): string {
  if (!input) return "";
  const trimmed = input.trim();
  if (!trimmed) return "";

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    // Not parseable as a URL — surface the trimmed string and let the
    // form's separate format validation surface the error.
    return trimmed;
  }

  const scheme = parsed.protocol.toLowerCase();
  const host = parsed.hostname.toLowerCase();

  // URL keeps explicit default ports (e.g. new URL("https://x.com:443").port
  // returns "443"), so we strip them manually to canonicalise. Non-default
  // ports are preserved verbatim — same machine on :8443 is a different
  // service surface to the same machine on :443 and shouldn't dedupe.
  let port = parsed.port;
  if (
    (scheme === "https:" && port === HTTPS_DEFAULT_PORT) ||
    (scheme === "http:" && port === HTTP_DEFAULT_PORT)
  ) {
    port = "";
  }

  // Strip trailing slashes from the path. URL.pathname for "https://x.com"
  // is "/"; after rstrip it becomes "" so the canonical form is just the
  // host. Multiple trailing slashes ("/foo//") collapse to one path
  // ("/foo") to match BE.
  const path = parsed.pathname.replace(/\/+$/, "");

  return `${scheme}//${host}${port ? `:${port}` : ""}${path}${parsed.search}${parsed.hash}`;
}

// Compare two URLs after normalization. Convenience wrapper so call
// sites don't have to remember to normalize both sides.
export function urlsAreEquivalent(a: string, b: string): boolean {
  return normalizeUrl(a) === normalizeUrl(b);
}
